import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
  Paper,
  CircularProgress,
  IconButton,
  Checkbox,
  Tooltip,
  SelectChangeEvent,
  Box,
  Chip,
  FormControlLabel,
  Typography,
  RadioGroup,
  Grid,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import _, { isEmpty } from "lodash";
import {
  DateValidationError,
  LocalizationProvider,
  PickerChangeHandlerContext,
} from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import {
  CustomSelectProps,
  CustomTextFieldProps,
  EditCustomTextFieldProps,
  SearchInputProps,
} from "../form-builder/form-config.model";
import {
  CustomStyledTextField,
  StyledFormControl,
  CustomStyledInputLabel,
  StyledMenuProps,
  CustomStyledDatePicker,
  CustomStyledRadio,
  CustomStyledCheckbox,
} from "../form-builder/form-styles";
import {
  CustomDatePickerProps,
  CustomRadioProps,
  CustomCheckboxProps,
  ChipsInputProps,
  CustomComponentFieldProps,
  CustomDateRangePickerProps,
} from "./form-elements.model";
import EditIcon from "@mui/icons-material/Edit";
import ReactDOM from "react-dom";
import { Close } from "@mui/icons-material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export const CustomTextField: React.FC<CustomTextFieldProps> = ({
  label,
  error,
  success = false,
  errorText,
  successText,
  variant = "outlined",
  helperText,
  required,
  size = "small",
  disabled,
  readOnly = false,
  ...props
}) => {
  // Determine the helper text based on error or success state
  // const displayHelperText = error ? errorText : success ? successText : helperText;

  // Determine the icon based on error or success state
  const endAdornment = error ? (
    <InputAdornment position="end">
      <ErrorIcon color="error" />
    </InputAdornment>
  ) : success ? (
    <InputAdornment position="end">
      <CheckCircleIcon color="success" />
    </InputAdornment>
  ) : null;

  return (
    <>
      {label && (
        <CustomStyledInputLabel
          required={required}
          error={error}
          disabled={disabled}
        >
          {label}
        </CustomStyledInputLabel>
      )}
      <CustomStyledTextField
        {...props}
        variant={variant}
        size={size}
        fullWidth
        error={error}
        helperText={helperText}
        disabled={disabled}
        InputProps={{
          endAdornment,
          readOnly: readOnly,
          ...props.InputProps,
        }}
        InputLabelProps={{
          shrink: false,
          ...props.InputLabelProps,
        }}
      />
    </>
  );
};

export const EditCustomTextField: React.FC<EditCustomTextFieldProps> = ({
  name,
  label,
  error,
  success = false,
  errorText,
  successText,
  variant = "outlined",
  helperText,
  required,
  size = "small",
  disabled,
  value = "",
  onSave,
  setFieldValue,
  readOnly = false,
  ...props
}) => {
  const [isEditable, setIsEditable] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);

  const displayHelperText = error
    ? errorText
    : success
    ? successText
    : helperText;

  useEffect(() => {
    if (value) setCurrentValue(value);
  }, [value]);

  const toggleEditable = () => {
    if (isEditable) {
      if (onSave && name) {
        onSave(name, currentValue); // Notify parent component
      }
      if (setFieldValue && name) {
        setFieldValue(name, currentValue); // Update Formik state
      }
    }
    setIsEditable(!isEditable);
  };

  const endAdornment = (
    <>
      <InputAdornment
        position="end"
        onClick={toggleEditable}
        style={{ cursor: "pointer" }}
      >
        {isEditable ? <CheckCircleIcon color="success" /> : <EditIcon />}
      </InputAdornment>
      {error && (
        <InputAdornment position="end">
          <ErrorIcon color="error" />
        </InputAdornment>
      )}
    </>
  );

  return (
    <>
      {label && (
        <CustomStyledInputLabel
          required={required}
          error={error}
          disabled={!isEditable || disabled}
        >
          {label}
        </CustomStyledInputLabel>
      )}
      <CustomStyledTextField
        {...props}
        variant={variant}
        size={size}
        fullWidth
        error={error}
        disabled={!isEditable || disabled}
        value={currentValue}
        onChange={(e) => setCurrentValue(e.target.value)}
        helperText={displayHelperText}
        InputProps={{
          readOnly: readOnly,
          ...props.InputProps,
        }}
        InputLabelProps={{
          shrink: false,
          ...props.InputLabelProps,
        }}
      />
    </>
  );
};
// CustomSelect Component
export const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  helperText,
  error = false,
  size = "small",
  required = false,
  placeholder,
  value,
  options = [],
  onChange,
  multiple = false,
  readOnly = false,
  enableSearch = false,
  isOptionDisabled, // New optional prop
  ...props
}) => {
  const selectRef = useRef<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Array<any>>(
    options || []
  ); // Ensure array
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setFilteredOptions(
      enableSearch && Array.isArray(options)
        ? options.filter((option) =>
            option.label?.toLowerCase().includes(searchQuery?.toLowerCase())
          )
        : Array.isArray(options)
        ? options
        : []
    );
  }, [searchQuery, options, enableSearch]);

  useEffect(() => {
    if (open && enableSearch && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, enableSearch]);
  const handleChange = (
    event: SelectChangeEvent<string | number | string[]>
  ) => {
    if (!readOnly) {
      onChange(event);
    }
    const newValue = event.target.value; // Current value from the event
    let allIndexes: any;
    let selectIndex: number[] = [];
    let unselectIndex: number[] = [];

    if (!multiple) {
      // Single selection
      const selectedIndex = options.findIndex(
        (option) => option.value === newValue
      );

      selectIndex = selectedIndex !== -1 ? [selectedIndex] : [];
      unselectIndex =
        value !== undefined
          ? [options.findIndex((option) => option.value === value)]
          : [];
      allIndexes = selectIndex;
    } else if (Array.isArray(newValue)) {
      // Multiple selection
      const currentSelectedIndexes = newValue.map((val) =>
        options.findIndex((option) => option.value === val)
      );

      const previousSelectedIndexes = (value as string[]).map((val) =>
        options.findIndex((option) => option.value === val)
      );

      // Determine newly selected and unselected indices
      selectIndex = currentSelectedIndexes.filter(
        (index) => !previousSelectedIndexes.includes(index)
      );
      unselectIndex = previousSelectedIndexes.filter(
        (index) => !currentSelectedIndexes.includes(index)
      );

      allIndexes = currentSelectedIndexes;
    }

    // Pass the indices to the onChange callback
    onChange(event, {
      select_index: selectIndex,
      unselect_index: unselectIndex,
    });
  };

  return (
    <StyledFormControl size={size} error={error} fullWidth>
      {label && (
        <CustomStyledInputLabel
          required={required}
          disabled={props.disabled ?? props.disabled}
        >
          {label}
        </CustomStyledInputLabel>
      )}
      <div ref={selectRef}>
        <Select
          {...props}
          multiple={multiple}
          value={value}
          displayEmpty
          fullWidth
          size={size}
          onChange={handleChange}
          open={open}
          onOpen={() => {
            setSearchQuery("");
            setOpen(true);
          }}
          onClose={() => setOpen(false)}
          renderValue={() => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "8px",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  flexGrow: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {multiple
                  ? Array.isArray(value) && value?.length > 0
                    ? value
                        ?.map((v) =>
                          Array.isArray(options)
                            ? options.find((o) => o?.value === v)?.label
                            : ""
                        )
                        .join(", ")
                    : placeholder
                  : (Array.isArray(options)
                      ? options.find((o) => o?.value === value)?.label
                      : "") || placeholder}
              </span>
              {value && !isEmpty(value) && !multiple && !props?.disabled && (
                <IconButton
                  size="small"
                  style={{ padding: "0" }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange({
                      target: { value: multiple ? [] : "" },
                    } as SelectChangeEvent);
                    setOpen(false); // Explicitly close the dropdown
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </div>
          )}
        >
          {enableSearch && (
            <MenuItem disableRipple disableTouchRipple>
              <TextField
                inputRef={inputRef}
                autoFocus
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </MenuItem>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option: any) => (
              <MenuItem 
                key={option.value} 
                value={option.value}
                disabled={isOptionDisabled ? isOptionDisabled(option, value) : false}
              >
                {multiple && (
                  <Checkbox
                    checked={
                      Array.isArray(value) && value.includes(option.value)
                    }
                  />
                )}
                {option.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No options available</MenuItem>
          )}
        </Select>
      </div>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </StyledFormControl>
  );
};

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  label,
  value,
  onChange,
  helperText,
  error,
  required,
  readOnly = false,
  maxDate,
  minDate,
  disableFuture = true,
}) => {
  const handleDateChange = (
    value: unknown,
    context: PickerChangeHandlerContext<DateValidationError>
  ) => {
    // Ensure value is either a Moment object or null
    if (moment.isMoment(value) || value === null) {
      const formattedDate = value ? value.format("YYYY-MM-DD") : null; // Format if valid
      onChange(formattedDate);
    } else {
      console.warn("Invalid date value received:", value);
      onChange(null); // Fallback to null if invalid type
    }
  };

  return (
    <FormControl fullWidth error={error}>
      {label && (
        <CustomStyledInputLabel required={required}>
          {label}
        </CustomStyledInputLabel>
      )}

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <CustomStyledDatePicker
          value={value ? moment(value) : null} // Convert value to Moment if not null
          onChange={handleDateChange} // Passes both value and context
          format="YYYY-MM-DD"
          readOnly={readOnly}
          // disabled={readOnly}

          minDate={minDate ? minDate : moment().subtract(100, "years")} // 100 years ago
          maxDate={maxDate ? maxDate : disableFuture ? moment() : undefined}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: "outlined",
              size: "small",
              InputLabelProps: { shrink: false },
              error: error,
              inputProps: {
                readOnly: readOnly, // Make the text input read-only
              },
            },
          }}
        />
      </LocalizationProvider>
      {error && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  label,
  value,
  onChange,
  helperText,
  error,
  required,
  readOnly = false,
  maxDate,
  minDate,
  disableFuture = true,
}) => {
  const handleStartDateChange = (
    newDate: unknown,
    context: PickerChangeHandlerContext<DateValidationError>
  ) => {
    // Create a new array instead of mutating the existing one
    const newValue: [Date | string | null, Date | string | null] = [...(value || [null, null])];
    // Ensure value is either a Moment object or null
    if (moment.isMoment(newDate) || newDate === null) {
      const formattedDate = newDate ? newDate.format("YYYY-MM-DD") : null; // Format if valid
      newValue[0] = formattedDate;
      onChange(newValue);
    } else {
      console.warn("Invalid date value received:", newDate);
      newValue[0] = null;
      onChange(newValue); // Fallback to null if invalid type
    }
  };

  const handleEndDateChange = (
    newDate: unknown,
    context: PickerChangeHandlerContext<DateValidationError>
  ) => {
    // Create a new array instead of mutating the existing one
    const newValue: [Date | string | null, Date | string | null] = [...(value || [null, null])];
    // Ensure value is either a Moment object or null
    if (moment.isMoment(newDate) || newDate === null) {
      const formattedDate = newDate ? newDate.format("YYYY-MM-DD") : null; // Format if valid
      newValue[1] = formattedDate;
      onChange(newValue);
    } else {
      console.warn("Invalid date value received:", newDate);
      newValue[1] = null;
      onChange(newValue); // Fallback to null if invalid type
    }
  };

  return (
    <FormControl error={error}>
      {label && (
        <CustomStyledInputLabel required={required}>
          {label}
        </CustomStyledInputLabel>
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <CustomStyledDatePicker
            value={value && value[0] ? moment(value[0]) : null} // Convert value to Moment if not null
            onChange={handleStartDateChange} // Passes both value and context
            format="YYYY-MM-DD"
            readOnly={readOnly}
            // disabled={readOnly}

            minDate={minDate ? minDate : moment().subtract(100, "years")} // 100 years ago
            maxDate={maxDate ? maxDate : disableFuture ? moment() : undefined}
            slotProps={{
              textField: {
                fullWidth: false,
                variant: "outlined",
                size: "small",
                InputLabelProps: { shrink: false },
                error: error,
                inputProps: {
                  readOnly: readOnly, // Make the text input read-only
                },
              },
            }}
          />
          <CustomStyledDatePicker
            value={value && value[1] ? moment(value[1]) : null} // Convert value to Moment if not null
            onChange={handleEndDateChange} // Passes both value and context
            format="YYYY-MM-DD"
            readOnly={readOnly}
            // disabled={readOnly}

            minDate={minDate ? minDate : moment().subtract(100, "years")} // 100 years ago
            maxDate={maxDate ? maxDate : disableFuture ? moment() : undefined}
            slotProps={{
              textField: {
                fullWidth: false,
                variant: "outlined",
                size: "small",
                InputLabelProps: { shrink: false },
                error: error,
                inputProps: {
                  readOnly: readOnly, // Make the text input read-only
                },
              },
            }}
          />
        </Box>
      </LocalizationProvider>
      {error && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

// SearchInput Component
export const SearchInput: React.FC<SearchInputProps> = ({
  size = "small",
  placeholder = "Search",
  error = false,
  success = false,
  fullWidth = false,
  onSearch,
  onChange,
  restrictInputFormat = false,
  label,
  required,
  value = "",
  param,
  displayKey, // Add displayKey to specify which key of the object should be shown
  readOnly = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState<string>(value);
  const [options, setOptions] = useState<any[]>([]); // Update to store an array of objects
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const [valueSelected, setValueSelected] = useState(false);
  const [autoDropdownPosition, setAutoDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  }>({
    top: 0,
    left: 0,
    width: 0,
  });
  const PAGE_SIZE = 10;
  // Sync inputValue with value prop
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || "");
    }
  }, [value]);
  // Debounced API call
  const fetchOptions = useCallback(
    _.debounce(async (query: string, offset: number) => {
      setLoading(true);
      try {
        const results = await onSearch(query, offset, param);
        if (offset === 0) {
          setOptions(results); // Reset options on a new search
        } else {
          setOptions((prevOptions) => [...prevOptions, ...results]); // Append on "Show More"
        }
        setHasMore(results.length === PAGE_SIZE);
        setDropdownVisible(true);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    }, 500),
    [onSearch]
  );
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setAutoDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };
  // Handle input change event
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = event.target.value;

    if (restrictInputFormat) {
      if (!/^(TE\d*|\d*)$/.test(inputVal)) {
        return; // Block invalid characters
      }
    }

    setInputValue(inputVal);
    setOffset(0);
    setValueSelected(false);
    // Trigger API call when input length is 2 or more
    if (inputVal.length >= 3) {
      fetchOptions(inputVal, 0);
    } else {
      setOptions([]); // Clear options if input length is less than 2
      setDropdownVisible(false);
    }
  };

  // Handle option selection
  const handleSelect = (selectedObj: any) => {
    setInputValue(selectedObj[displayKey]);
    setOptions([]);
    setValueSelected(true);
    setTimeout(() => {
      setDropdownVisible(false); // ✅ Delay closing dropdown slightly
    }, 50);

    onChange(selectedObj);
  };

  // Handle "Show More" button click
  const handleShowMore = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setLoadingMore(true);
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    fetchOptions(inputValue, newOffset);
  };

  // Handle clear input
  const handleClearInput = () => {
    setInputValue("");
    setOptions([]);
    setDropdownVisible(false);
    onChange(null); // Clear the selected value in the parent
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".menu-item-option") // ✅ Prevent closing when clicking on options
      ) {
        setTimeout(() => {
          setDropdownVisible(false);
        }, 50);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    updateDropdownPosition();
  }, [dropdownVisible]);
  const onBlurAction = () => {
    if (!valueSelected) {
      setInputValue("");
    }
  };
  // Render component
  return (
    <div ref={inputRef} style={{ position: "relative" }}>
      <StyledFormControl size={size} error={error} fullWidth>
        {label && (
          <CustomStyledInputLabel required={required}>
            {label}
          </CustomStyledInputLabel>
        )}
        <CustomStyledTextField
          {...props}
          variant="outlined"
          size={size}
          required={required}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          error={error}
          success={success}
          fullWidth={fullWidth}
          onBlur={onBlurAction}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            readOnly: readOnly,
            startAdornment: (
              <InputAdornment position="start">
                {!restrictInputFormat ? (
                  <SearchIcon color="disabled" />
                ) : (
                  <>
                    <SearchIcon color="disabled" />
                    <strong>TE-</strong>
                  </>
                )}
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <>
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    inputValue && (
                      <IconButton
                        disabled={readOnly}
                        size="small"
                        onClick={handleClearInput}
                        aria-label="clear input"
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                  )}
                </>
              </InputAdornment>
            ),
          }}
        />
        {(dropdownVisible || loading) &&
          ReactDOM.createPortal(
            <Paper
              elevation={2}
              style={{
                position: "absolute",
                top: inputRef.current
                  ? inputRef.current.getBoundingClientRect().bottom +
                    window.scrollY
                  : "100%",
                left: inputRef.current
                  ? inputRef.current.getBoundingClientRect().left +
                    window.scrollX
                  : 0,
                width: inputRef.current ? inputRef.current.offsetWidth : "100%",
                maxHeight: "200px",
                overflowY: "auto",
                zIndex: 9999, // ✅ Ensure it's above table elements
                borderRadius: 8,
              }}
            >
              {options?.length > 0 ? (
                options.map((option, index) => (
                  <MenuItem
                    className="menu-item-option"
                    key={index}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      handleSelect(option); // ✅ Call existing selection function
                      setInputValue(option[displayKey]); // ✅ Update input value immediately
                    }}
                  >
                    {option[displayKey]}
                  </MenuItem>
                ))
              ) : !loading ? (
                <MenuItem disabled={true}>No Results</MenuItem>
              ) : (
                <></>
              )}
              {loading && (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              )}
              {hasMore && !loading && (
                <MenuItem
                  style={{
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  onClick={handleShowMore}
                  onMouseDown={handleShowMore}
                >
                  {loadingMore ? <CircularProgress size={20} /> : "Show More"}
                </MenuItem>
              )}
            </Paper>,
            document.body
          )}
      </StyledFormControl>
    </div>
  );
};

// CustomRadio Component
export const CustomRadio: React.FC<CustomRadioProps> = ({
  className,
  label,
  value,
  onChange,
  options,
  helperText,
  error,
  required,
  disabled,
  readOnly = false,
  ...props
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!readOnly && onChange) {
      onChange(event);
    }
  };
  return (
    <>
      {label && (
        <CustomStyledInputLabel required={required} error={error}>
          {label}
        </CustomStyledInputLabel>
      )}
      <FormControl component="fieldset" error={error} className={className}>
        <RadioGroup value={value} onChange={handleChange} {...props} row>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              control={<CustomStyledRadio />}
              label={option.label}
              value={option.value}
            />
          ))}
        </RadioGroup>
        {error && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </>
  );
};

// CustomCheckbox Component
export const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  className,
  label,
  checked,
  onChange,
  onBlur,
  helperText,
  error,
  readOnly = false,
  ...props
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!readOnly && onChange) {
      onChange(event);
    }
  };
  return (
    <>
      <FormControlLabel
        className={className}
        control={
          <CustomStyledCheckbox
            checked={checked}
            onChange={handleChange}
            onBlur={onBlur}
            {...props} // Spread any additional props to Checkbox
          />
        }
        label={label}
      />
      {error && <FormHelperText>{helperText}</FormHelperText>}
    </>
  );
};

export const ChipsInput: React.FC<ChipsInputProps> = ({
  value,
  onChange,
  label,
  error,
  required,
  helperText,
  readOnly = false,
  ...props
}) => {
  const [inputValue, setInputValue] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);

  const handleAddChip = () => {
    if (inputValue.trim() && !value.includes(inputValue) && !readOnly) {
      onChange([...value, inputValue.trim()]);
      setInputValue("");
    }
  };

  const handleRemoveChip = (chip: string) => {
    if (!readOnly) {
      onChange(value.filter((item) => item !== chip));
    }
  };

  return (
    <Box>
      {label && (
        <>
          <CustomStyledInputLabel required={required} error={error}>
            {label}
            <span className="disclaimer">
              {" (Press Enter on keyboard after each value)"}
            </span>
          </CustomStyledInputLabel>
        </>
      )}
      {value.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, marginY: 1 }}>
          {value?.map((chip) => (
            <Chip
              key={chip}
              label={chip}
              onDelete={() => handleRemoveChip(chip)}
              sx={{ backgroundColor: "#F0CD7F", fontSize: "14px" }}
            />
          ))}
        </Box>
      )}
      <Tooltip
        title="Press Enter to add the value"
        open={!!inputValue}
        placement="bottom-start"
        arrow
      >
        <CustomStyledTextField
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddChip();
            }
          }}
          InputProps={{
            readOnly: readOnly,
          }}
          size="small"
          error={error}
          helperText={helperText}
          fullWidth
          onBlur={() => {
            handleAddChip();
            setIsFocused(false);
          }}
          onFocus={() => setIsFocused(true)}
          InputLabelProps={{
            shrink: false,
          }}
          {...props}
        />
      </Tooltip>
    </Box>
  );
};

export const CustomComponent: React.FC<CustomComponentFieldProps> = ({
  label,
  children,
  error,
  ...props
}) => {
  return (
    <>
      {label && (
        <CustomStyledInputLabel error={error}>{label}</CustomStyledInputLabel>
      )}

      <Grid>
        {children}
        {error && (
          <span
            className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeSmall MuiFormHelperText-contained css-1idtn9v-MuiFormHelperText-root css-1idtn9v-MuiFormHelperText-root.Mui-error"
            style={{ display: "flex", justifyContent: "center" }}
          >
            Non Certified Trader Info is required
          </span>
        )}
      </Grid>
    </>
  );
};
